import React, { useState, MouseEvent, useEffect } from "react";
import IconApp from "../../Icons/AppIcon";
import IconScheduling from "../../Icons/SideMenu/Calendar";
import IconImager from "../../Icons/SideMenu/Imager";
import IconOverview from "../../Icons/SideMenu/Overview";
import IconPlate from "../../Icons/SideMenu/Plate";
import IconPlateTypeEditor from "../../Icons/SideMenu/PlateTypeEditor";
import IconReportAndSupport from "../../Icons/SideMenu/ReportAndSupport";
import IconServicing from "../../Icons/SideMenu/Servicing";
import IconTemperature from "../../Icons/SideMenu/Temperature";
import IconTools from "../../Icons/SideMenu/Tools";
import { EComponentId, PermissionProps } from "../../Model/Permission";
import PermissionUtil from "../../Utility/PermissionUtil";
import { usePermission } from "../Common/PermissionProvider";
import { NavItemWithPermission } from "./NavItem";
import "./Sidebar.css";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import { getModuleStateById } from "../../redux/reducers/ModuleStateReducer";
import { ESuiteMode } from "../../Model/ModuleState";
import { EImagingMode } from "../../Model/EImagingMode";
import BlockAndChangeToManualModeModal from "./BlockAndChangeToManualModeModal";
import { useHistory } from "react-router-dom";
import { isAuthenticationEnable, isSupportModeEnabled } from "../../redux/reducers/AuthenticationSettingReducer";
import { useDispatch } from "react-redux";
import { authenticationSetSupportMode } from "../../redux/actions/AuthenticationSettingAction";

const getIsUrlNeedManualMode = (url: string) => {
  return url.includes("/tools") || url.includes("/settings");
};

interface Props extends PermissionProps { }

const Sidebar = ({ permissions, componentId }: Props) => {
  const dispatch = useDispatch();
  const isAuthEnable: boolean = useTypedSelector((state) => isAuthenticationEnable(state));
  const isEnableSupportMode = useTypedSelector(state => isSupportModeEnabled(state));
  const suiteState = useTypedSelector(state => getModuleStateById(state)["Suite"].suite);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.ctrlKey &&
        event.shiftKey &&
        event.key.toLowerCase() === 's'
      ) {
        event.preventDefault();
        console.log('Ctrl + Shift + S pressed!');
        dispatch(authenticationSetSupportMode(!isEnableSupportMode));
      }
    };

    if (isAuthEnable || (suiteState?.alwaysShowSettingsAndTools ?? false)) {
      return; // Do not add the event listener if authentication is enabled
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAuthEnable, isEnableSupportMode, dispatch]);


  const plateMenuChildPermissionIds = [
    EComponentId.PlateLoadPort,
    EComponentId.PlateSearch,
  ];
  const imagerMenuChildPermissionIds = [
    EComponentId.ImagerLiveImage,
    EComponentId.ImagerEfi,
    EComponentId.ImagerDropLocation,
    EComponentId.ImagerFocusGraph,
    EComponentId.ImagerLastCaptured,
  ];
  const plateSetupMenuChildPermissionIds = [
    EComponentId.PlateSetupPlateTypeCalibration,
    EComponentId.PlateSetupDefImgSetting,
    EComponentId.PlateSetupImageMask,
  ];
  const servicingMenuChildPermissionIds = [
    EComponentId.SettingsVectorEditor,
    EComponentId.SettingsStorage,
    EComponentId.SettingsImager,
    EComponentId.SettingsPsrCamera,
    EComponentId.SettingsSystemBackup,
    EComponentId.SettingsPreferences,
  ];
  const reportAndSupportMenuChildPermissionIds = [
    EComponentId.ReportAndSupportEventLog,
    EComponentId.ReportAndSupportLicensing,
    EComponentId.ReportAndSupportUserManagement,
  ];
  const toolsMenuChildPermissionIds = [
    EComponentId.ToolsStoreAndRetrieve,
    EComponentId.ToolsGeneral,
    EComponentId.ToolsEmail,
    EComponentId.ToolsTesting,
  ];

  const history = useHistory();
  const [showCantEnter, setShowCantEnter] = useState(false);
  const [redirectOkUrl, setRedirectOkUrl] = useState(
    undefined as string | undefined
  );
  const [redirectCancelUrl, setRedirectCancelUrl] = useState(
    undefined as string | undefined
  );

  const isTesting = useTypedSelector(
    (state) =>
      getModuleStateById(state)["Suite"].suite?.suiteMode == ESuiteMode.Testing
  );

  const isAutoCalibrating = useTypedSelector(
    (state) =>
      getModuleStateById(state)["Suite"].suite?.suiteMode ==
      ESuiteMode.AutoCalibrating
  );

  const isImagingManualMode = useTypedSelector(
    (state) =>
      getModuleStateById(state)["Suite"].suite?.imagingMode ==
      EImagingMode.Manual
  );

  const OverrideOnClickImagingManualMode = (
    e: MouseEvent<HTMLAnchorElement>
  ) => {
    // validateManualMode
    if (!isImagingManualMode) {
      // disable route
      e.preventDefault();
      setShowCantEnter(true);
      return;
    }
  };

  useEffect(() => {
    if (
      getIsUrlNeedManualMode(history.location.pathname) &&
      !isImagingManualMode
    ) {
      setRedirectOkUrl(history.location.pathname);
      setRedirectCancelUrl("/");
      setShowCantEnter(true);
    } else {
      setShowCantEnter(false);
    }
  }, [history.location.pathname, isImagingManualMode]);

  const allowOpenSettings = isAuthEnable || isEnableSupportMode || (suiteState?.alwaysShowSettingsAndTools ?? false);

  return (
    <div
      style={{
        height: "100%",
        overflow: "hidden",
      }}
    >
      <div className="sidebar">
        <div className="app-icon">
          <IconApp
            style={{
              margin: "auto",
            }}
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
          />
        </div>
        <NavItemWithPermission
          componentId={EComponentId.SystemOverview}
          label="System Overview"
          key="systemOverview"
          to="/"
          icon={<IconOverview />}
          disabled={isTesting || isAutoCalibrating}
        />
        <NavItemWithPermission
          componentId={PermissionUtil.getFirstCanReadPermissionComponentId(
            permissions,
            plateMenuChildPermissionIds
          )}
          label="Plates"
          key="loadport"
          to="/plate"
          icon={<IconPlate />}
          disabled={isTesting || isAutoCalibrating}
        />
        <NavItemWithPermission
          componentId={PermissionUtil.getFirstCanReadPermissionComponentId(
            permissions,
            imagerMenuChildPermissionIds
          )}
          label="Imager"
          key="imager"
          to={"/imager"}
          icon={<IconImager />}
          disabled={isTesting || isAutoCalibrating}
        />
        <NavItemWithPermission
          componentId={EComponentId.Schedule}
          label="Schedule"
          key="schedule"
          to="/schedule"
          icon={<IconScheduling />}
          disabled={isTesting || isAutoCalibrating}
        />
        <NavItemWithPermission
          componentId={EComponentId.Temperature}
          label="Temperature"
          key="temperature"
          to="/temperature"
          icon={<IconTemperature />}
          disabled={isTesting || isAutoCalibrating}
        />
        <NavItemWithPermission
          componentId={PermissionUtil.getFirstCanReadPermissionComponentId(
            permissions,
            plateSetupMenuChildPermissionIds
          )}
          label="Plate Setup"
          key="palteTypeEditor"
          to="/plate-type-editor"
          icon={<IconPlateTypeEditor />}
          disabled={isTesting || isAutoCalibrating}
        />
        {allowOpenSettings && <NavItemWithPermission
          componentId={PermissionUtil.getFirstCanReadPermissionComponentId(
            permissions,
            toolsMenuChildPermissionIds
          )}
          label="Tools"
          key="tools"
          to="/tools"
          icon={<IconTools />}
          disabled={isAutoCalibrating}
          onClick={(e) => {
            setRedirectOkUrl("/tools");
            setRedirectCancelUrl(undefined);
            OverrideOnClickImagingManualMode(e);
          }}
        />
        }
        {allowOpenSettings &&
          <NavItemWithPermission
            componentId={PermissionUtil.getFirstCanReadPermissionComponentId(
              permissions,
              servicingMenuChildPermissionIds
            )}
            label="Settings"
            key="servicing"
            to="/settings"
            icon={<IconServicing />}
            disabled={isTesting}
            onClick={(e) => {
              setRedirectOkUrl("/settings");
              setRedirectCancelUrl(undefined);
              OverrideOnClickImagingManualMode(e);
            }}
          />}
        <NavItemWithPermission
          componentId={PermissionUtil.getFirstCanReadPermissionComponentId(
            permissions,
            reportAndSupportMenuChildPermissionIds
          )}
          label="Report and Support"
          key="reportAndSupport"
          to="/report-support"
          icon={<IconReportAndSupport />}
          disabled={isTesting || isAutoCalibrating}
        />

        {/* <NavItem label="Setup" to="/setup" icon={<IconSetup />} /> */}
        {/* <NavItem label="Help" to="/help" icon={<IconHelp />}/> */}

        <BlockAndChangeToManualModeModal
          show={showCantEnter}
          onHide={() => setShowCantEnter(false)}
          redirectOkTo={redirectOkUrl}
          redirectCancelTo={redirectCancelUrl}
          name="sidebar"
        />
      </div>
    </div>
  );
};

export const SidebarWithPermission = usePermission(Sidebar);
export default Sidebar;
